/**
 * Acquisition deal model: sources & uses, debt service, DSCR, what the buyer
 * takes home, the most the cash flow can support, and returns on the buyer's
 * equity. Pure and synchronous so the editor can recalculate on every
 * keystroke and the API can recompute the same numbers. Pre-tax throughout.
 */

import { monthlyPayment } from "./amortization";
import { DEAL_MODEL_DEFAULTS, SBA_DEFAULTS } from "./sba-defaults";

export type DealModelLoan = {
  amount: number;
  /** Annual rate, e.g. 0.105. */
  rate: number;
  termYears: number;
};

export type DealModelInputs = {
  schemaVersion: 1;
  uses: {
    purchasePrice: number;
    workingCapital: number;
    closingCosts: number;
    other: number;
  };
  sources: {
    buyerEquity: number;
    /** "fill" sizes the SBA loan to close the gap between uses and other sources. */
    sba: DealModelLoan & { mode: "fill" | "amount"; guaranteeFeePct: number };
    /** No payments during standby; interest accrues and is added to the balance. */
    sellerNote: DealModelLoan & { standbyYears: number };
    otherDebt: DealModelLoan;
    /** Part of the price paid from cash flow after close, in equal yearly amounts. */
    earnout: { amount: number; years: number };
  };
  operations: {
    sde: number;
    /** What you pay yourself; lenders deduct it. Null until entered. */
    salary: number | null;
    capex: number;
    growthPct: number;
  };
  exit: { holdYears: number; multiple: number | null };
  assumptions: { minDscr: number; stressPct: number };
};

export type DealModelWarning =
  | "FUNDING_GAP"
  | "FUNDING_SURPLUS"
  | "NO_SALARY"
  | "BELOW_MIN_DSCR"
  | "STRESS_BELOW_MIN_DSCR"
  | "INJECTION_SHORT"
  | "EARNOUT_WITH_SBA"
  | "NEGATIVE_CASH_FLOW";

export interface DealModelYear {
  year: number;
  sde: number;
  /** SDE less your salary and capex: what services the debt. */
  cashAvailable: number;
  debtService: number;
  earnoutPayment: number;
  dscr: number | null;
  /** Left after debt service and earnout, on top of your salary. */
  cashToBuyer: number;
  debtBalance: number;
}

export interface DealModelResult {
  sourcesAndUses: {
    uses: { key: string; label: string; amount: number }[];
    sources: { key: string; label: string; amount: number }[];
    totalUses: number;
    totalSources: number;
    /** Sources less uses: negative is a gap to fund. */
    difference: number;
  };
  sbaLoan: { principal: number; guaranteeFee: number };
  years: DealModelYear[];
  dscr: number | null;
  stressedDscr: number | null;
  minDscr: number;
  bankable: boolean;
  takeHome: { salary: number; cashFlow: number; total: number };
  injection: {
    applies: boolean;
    required: number;
    counted: number;
    short: number;
  };
  /** Highest price at the minimum DSCR, keeping this structure; null when debt doesn't limit it. */
  maxSupportablePrice: number | null;
  returns: {
    equity: number;
    cashOnCash: number | null;
    irr: number | null;
    moic: number | null;
    exitValue: number | null;
    exitProceeds: number | null;
  };
  sensitivity: {
    priceChanges: number[];
    sdeChanges: number[];
    /** dscr[priceIndex][sdeIndex] */
    dscr: (number | null)[][];
  };
  warnings: DealModelWarning[];
}

const SENSITIVITY_STEPS = [-0.2, -0.1, 0, 0.1, 0.2];

const clean = (value: number) =>
  Number.isFinite(value) && value > 0 ? value : 0;

/**
 * Equity that meets the injection when the SBA loan fills the rest. The
 * requirement is a share of total cost, which includes the financed
 * guarantee fee, which itself depends on the loan: E = p·U(1+f) / (1+p·f).
 */
function requiredInjection(uses: number) {
  const p = SBA_DEFAULTS.equityInjectionPct;
  const f = SBA_DEFAULTS.guaranteeFeePct;
  return Math.ceil((p * uses * (1 + f)) / (1 + p * f));
}

/** A starting model from a price and SDE, with typical SBA terms. */
export function defaultDealModelInputs(input: {
  purchasePrice: number | null;
  sde: number | null;
}): DealModelInputs {
  const price = clean(input.purchasePrice ?? 0);
  const closingCosts = Math.round(price * DEAL_MODEL_DEFAULTS.closingCostsPct);
  return {
    schemaVersion: 1,
    uses: { purchasePrice: price, workingCapital: 0, closingCosts, other: 0 },
    sources: {
      buyerEquity: requiredInjection(price + closingCosts),
      sba: {
        mode: "fill",
        amount: 0,
        rate: SBA_DEFAULTS.interestRate,
        termYears: SBA_DEFAULTS.termYears,
        guaranteeFeePct: SBA_DEFAULTS.guaranteeFeePct,
      },
      sellerNote: {
        amount: 0,
        rate: DEAL_MODEL_DEFAULTS.sellerNoteRate,
        termYears: DEAL_MODEL_DEFAULTS.sellerNoteTermYears,
        standbyYears: 0,
      },
      otherDebt: {
        amount: 0,
        rate: DEAL_MODEL_DEFAULTS.otherDebtRate,
        termYears: DEAL_MODEL_DEFAULTS.otherDebtTermYears,
      },
      earnout: { amount: 0, years: 3 },
    },
    operations: {
      sde: clean(input.sde ?? 0),
      salary: null,
      capex: 0,
      growthPct: 0,
    },
    exit: { holdYears: DEAL_MODEL_DEFAULTS.holdYears, multiple: null },
    assumptions: {
      minDscr: SBA_DEFAULTS.minDscr,
      stressPct: SBA_DEFAULTS.stressPct,
    },
  };
}

interface Tranche {
  principal: number;
  rate: number;
  termYears: number;
  delayYears: number;
}

interface Structure {
  closingPrice: number;
  sbaBase: number;
  guaranteeFee: number;
  tranches: Tranche[];
  totalUses: number;
  totalSources: number;
}

function buildStructure(inputs: DealModelInputs): Structure {
  const { uses, sources } = inputs;
  const earnout = Math.min(
    clean(sources.earnout.amount),
    clean(uses.purchasePrice),
  );
  const closingPrice = clean(uses.purchasePrice) - earnout;
  const baseUses =
    closingPrice +
    clean(uses.workingCapital) +
    clean(uses.closingCosts) +
    clean(uses.other);
  const otherSources =
    clean(sources.buyerEquity) +
    clean(sources.sellerNote.amount) +
    clean(sources.otherDebt.amount);
  const sbaBase =
    sources.sba.mode === "fill"
      ? Math.max(0, baseUses - otherSources)
      : clean(sources.sba.amount);
  // The guarantee fee is financed: it is both a use and part of the loan.
  const guaranteeFee = sbaBase * clean(sources.sba.guaranteeFeePct);
  return {
    closingPrice,
    sbaBase,
    guaranteeFee,
    tranches: [
      {
        principal: sbaBase + guaranteeFee,
        rate: clean(sources.sba.rate),
        termYears: clean(sources.sba.termYears),
        delayYears: 0,
      },
      {
        principal: clean(sources.sellerNote.amount),
        rate: clean(sources.sellerNote.rate),
        termYears: clean(sources.sellerNote.termYears),
        delayYears: clean(sources.sellerNote.standbyYears),
      },
      {
        principal: clean(sources.otherDebt.amount),
        rate: clean(sources.otherDebt.rate),
        termYears: clean(sources.otherDebt.termYears),
        delayYears: 0,
      },
    ].filter((tranche) => tranche.principal > 0),
    totalUses: baseUses + guaranteeFee,
    totalSources: otherSources + sbaBase + guaranteeFee,
  };
}

/** Payments made in each year and the balance left at the end of it. */
function scheduleTranche(tranche: Tranche, years: number) {
  const r = tranche.rate / 12;
  const delayMonths = Math.round(tranche.delayYears * 12);
  const termMonths = Math.round(tranche.termYears * 12);
  const payments = new Array<number>(years).fill(0);
  const balances = new Array<number>(years).fill(0);
  let balance = tranche.principal;
  let payment = 0;
  for (let month = 1; month <= years * 12; month++) {
    const year = Math.ceil(month / 12) - 1;
    if (month <= delayMonths) {
      balance *= 1 + r;
    } else if (month <= delayMonths + termMonths && balance > 0) {
      if (month === delayMonths + 1) {
        payment = monthlyPayment(balance, tranche.rate, tranche.termYears);
      }
      const interest = balance * r;
      const paid = Math.min(payment, balance + interest);
      balance = Math.max(0, balance + interest - paid);
      payments[year] = (payments[year] ?? 0) + paid;
    }
    if (month % 12 === 0) balances[year] = balance;
  }
  return { payments, balances };
}

function project(inputs: DealModelInputs, structure: Structure, years: number) {
  const { operations, sources } = inputs;
  const schedules = structure.tranches.map((tranche) =>
    scheduleTranche(tranche, years),
  );
  const earnout = clean(sources.earnout.amount);
  const earnoutYears = Math.max(1, Math.round(clean(sources.earnout.years)));
  const salary = clean(operations.salary ?? 0);
  const rows: DealModelYear[] = [];
  for (let i = 0; i < years; i++) {
    const sde = clean(operations.sde) * (1 + operations.growthPct) ** i;
    const cashAvailable = sde - salary - clean(operations.capex);
    const debtService = schedules.reduce(
      (sum, schedule) => sum + (schedule.payments[i] ?? 0),
      0,
    );
    const earnoutPayment = i < earnoutYears ? earnout / earnoutYears : 0;
    rows.push({
      year: i + 1,
      sde,
      cashAvailable,
      debtService,
      earnoutPayment,
      dscr: debtService > 0 ? cashAvailable / debtService : null,
      cashToBuyer: cashAvailable - debtService - earnoutPayment,
      debtBalance: schedules.reduce(
        (sum, schedule) => sum + (schedule.balances[i] ?? 0),
        0,
      ),
    });
  }
  return rows;
}

/** Year-one DSCR alone: cheap enough for every card in a list. */
export function yearOneDscr(inputs: DealModelInputs): number | null {
  return project(inputs, buildStructure(inputs), 1)[0]?.dscr ?? null;
}

/**
 * Coverage at the asking price with a default model (typical SBA terms,
 * before the buyer's salary). Null unless both figures are disclosed.
 */
export function estimateListingFinancing(input: {
  askingPrice: number | null;
  sde: number | null;
}) {
  if (
    !input.askingPrice ||
    input.askingPrice <= 0 ||
    !input.sde ||
    input.sde <= 0
  ) {
    return null;
  }
  const dscr = yearOneDscr(
    defaultDealModelInputs({
      purchasePrice: input.askingPrice,
      sde: input.sde,
    }),
  );
  return dscr == null ? null : { dscr, bankable: dscr >= SBA_DEFAULTS.minDscr };
}

function scaled(inputs: DealModelInputs, priceFactor: number, sdeFactor = 1) {
  const s = inputs.sources;
  return {
    ...inputs,
    uses: {
      ...inputs.uses,
      purchasePrice: inputs.uses.purchasePrice * priceFactor,
    },
    sources: {
      ...s,
      buyerEquity: s.buyerEquity * priceFactor,
      sba: { ...s.sba, amount: s.sba.amount * priceFactor },
      sellerNote: {
        ...s.sellerNote,
        amount: s.sellerNote.amount * priceFactor,
      },
      otherDebt: { ...s.otherDebt, amount: s.otherDebt.amount * priceFactor },
      earnout: { ...s.earnout, amount: s.earnout.amount * priceFactor },
    },
    operations: {
      ...inputs.operations,
      sde: inputs.operations.sde * sdeFactor,
    },
  };
}

/**
 * Highest price whose year-one DSCR still meets the minimum, scaling equity
 * and every source with the price. Null when there's no debt to limit it.
 */
function maxSupportablePrice(inputs: DealModelInputs): number | null {
  const price = inputs.uses.purchasePrice;
  const min = inputs.assumptions.minDscr;
  if (price <= 0) return null;
  const meets = (factor: number) => {
    const dscr = yearOneDscr(scaled(inputs, factor));
    return dscr == null || dscr >= min;
  };
  if (!meets(0.0001)) return 0;
  // Double until the minimum is missed, then bisect between the last two.
  let high = 1;
  while (meets(high)) {
    high *= 2;
    if (high > 1024) return null;
  }
  let low = high === 1 ? 0 : high / 2;
  for (let i = 0; i < 60; i++) {
    const mid = (low + high) / 2;
    if (meets(mid)) low = mid;
    else high = mid;
  }
  return Math.floor(price * low);
}

/** Internal rate of return of yearly cash flows starting at year 0. */
export function irr(cashFlows: number[]): number | null {
  const npv = (rate: number) =>
    cashFlows.reduce((sum, flow, year) => sum + flow / (1 + rate) ** year, 0);
  let low = -0.99;
  let high = 10;
  if (npv(low) * npv(high) > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    if (npv(low) * npv(mid) <= 0) high = mid;
    else low = mid;
  }
  return (low + high) / 2;
}

export function computeDealModel(inputs: DealModelInputs): DealModelResult {
  const structure = buildStructure(inputs);
  const { uses, sources, operations, assumptions } = inputs;
  const holdYears = Math.max(1, Math.round(clean(inputs.exit.holdYears)) || 1);
  const years = project(inputs, structure, holdYears);
  const first = years[0];
  const dscr = first?.dscr ?? null;
  const stressedDscr =
    project(
      {
        ...inputs,
        operations: {
          ...operations,
          sde: operations.sde * (1 - assumptions.stressPct),
        },
      },
      structure,
      1,
    )[0]?.dscr ?? null;
  const salary = clean(operations.salary ?? 0);

  // Equity injection only matters when there is an SBA loan.
  const injectionApplies = structure.sbaBase > 0;
  const required = injectionApplies
    ? structure.totalUses * SBA_DEFAULTS.equityInjectionPct
    : 0;
  const standbyCounts =
    clean(sources.sellerNote.standbyYears) >= clean(sources.sba.termYears);
  const counted =
    clean(sources.buyerEquity) +
    (standbyCounts
      ? Math.min(
          clean(sources.sellerNote.amount),
          required * SBA_DEFAULTS.standbyNoteInjectionShare,
        )
      : 0);

  const equity = clean(sources.buyerEquity);
  const last = years[years.length - 1];
  const exitValue =
    inputs.exit.multiple != null && last
      ? clean(inputs.exit.multiple) * last.sde
      : null;
  const exitProceeds =
    exitValue != null && last ? exitValue - last.debtBalance : null;
  const flows = [-equity, ...years.map((year) => year.cashToBuyer)];
  if (exitProceeds != null) flows[flows.length - 1] += exitProceeds;
  const inflows = flows.slice(1).reduce((sum, flow) => sum + flow, 0);

  const warnings: DealModelWarning[] = [];
  const difference = structure.totalSources - structure.totalUses;
  if (difference < -0.5) warnings.push("FUNDING_GAP");
  if (difference > 0.5) warnings.push("FUNDING_SURPLUS");
  if (operations.salary == null) warnings.push("NO_SALARY");
  if (dscr != null && dscr < assumptions.minDscr)
    warnings.push("BELOW_MIN_DSCR");
  if (
    stressedDscr != null &&
    stressedDscr < assumptions.minDscr &&
    !(dscr != null && dscr < assumptions.minDscr)
  ) {
    warnings.push("STRESS_BELOW_MIN_DSCR");
  }
  if (injectionApplies && counted < required - 0.5) {
    warnings.push("INJECTION_SHORT");
  }
  if (
    !SBA_DEFAULTS.earnoutAllowed &&
    structure.sbaBase > 0 &&
    clean(sources.earnout.amount) > 0
  ) {
    warnings.push("EARNOUT_WITH_SBA");
  }
  if (first && first.cashToBuyer < 0) warnings.push("NEGATIVE_CASH_FLOW");

  return {
    sourcesAndUses: {
      uses: [
        {
          key: "price",
          label: "Purchase price at close",
          amount: structure.closingPrice,
        },
        {
          key: "workingCapital",
          label: "Working capital",
          amount: clean(uses.workingCapital),
        },
        {
          key: "closingCosts",
          label: "Closing costs",
          amount: clean(uses.closingCosts),
        },
        { key: "other", label: "Other", amount: clean(uses.other) },
        {
          key: "guaranteeFee",
          label: "SBA guarantee fee",
          amount: structure.guaranteeFee,
        },
      ].filter((line) => line.amount > 0 || line.key === "price"),
      sources: [
        {
          key: "equity",
          label: "Your equity",
          amount: clean(sources.buyerEquity),
        },
        {
          key: "sba",
          label: "SBA loan",
          amount: structure.sbaBase + structure.guaranteeFee,
        },
        {
          key: "sellerNote",
          label: "Seller note",
          amount: clean(sources.sellerNote.amount),
        },
        {
          key: "otherDebt",
          label: "Other debt",
          amount: clean(sources.otherDebt.amount),
        },
      ].filter((line) => line.amount > 0),
      totalUses: structure.totalUses,
      totalSources: structure.totalSources,
      difference,
    },
    sbaLoan: {
      principal: structure.sbaBase + structure.guaranteeFee,
      guaranteeFee: structure.guaranteeFee,
    },
    years,
    dscr,
    stressedDscr,
    minDscr: assumptions.minDscr,
    bankable: dscr == null || dscr >= assumptions.minDscr,
    takeHome: {
      salary,
      cashFlow: first?.cashToBuyer ?? 0,
      total: salary + (first?.cashToBuyer ?? 0),
    },
    injection: {
      applies: injectionApplies,
      required,
      counted,
      short: Math.max(0, required - counted),
    },
    maxSupportablePrice: maxSupportablePrice(inputs),
    returns: {
      equity,
      cashOnCash: equity > 0 && first ? first.cashToBuyer / equity : null,
      irr: equity > 0 ? irr(flows) : null,
      moic: equity > 0 ? inflows / equity : null,
      exitValue,
      exitProceeds,
    },
    sensitivity: {
      priceChanges: SENSITIVITY_STEPS,
      sdeChanges: SENSITIVITY_STEPS,
      dscr: SENSITIVITY_STEPS.map((price) =>
        SENSITIVITY_STEPS.map((sde) =>
          yearOneDscr(scaled(inputs, 1 + price, 1 + sde)),
        ),
      ),
    },
    warnings,
  };
}
